import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Battery, Fuel, MapPin, Navigation } from 'lucide-react'
import type { LatLngExpression } from 'leaflet'
import type { Car } from '@/components/cars-map'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { localizeDigits } from '@/lib/localize-digits'

interface CarDetailsOverlayProps {
  car: Car
  userLocation: LatLngExpression | null
  accountName: string | null
  onReserve: () => void
  onClose: () => void
}

function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getFuelColor(level: number): string {
  if (level >= 60) return 'text-green-600 dark:text-green-400'
  if (level >= 30) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

export function CarDetailsOverlay({
  car,
  userLocation,
  accountName,
  onReserve,
  onClose,
}: CarDetailsOverlayProps) {
  const { t } = useTranslation()

  const isElectric = car.vehiclePropulsionTypeId === 2
  const carLat = Array.isArray(car.location)
    ? (car.location as [number, number])[0]
    : 0
  const carLng = Array.isArray(car.location)
    ? (car.location as [number, number])[1]
    : 0

  const distance = useMemo(() => {
    if (!userLocation) return null
    const userLat = Array.isArray(userLocation)
      ? (userLocation as [number, number])[0]
      : 0
    const userLng = Array.isArray(userLocation)
      ? (userLocation as [number, number])[1]
      : 0
    return haversineDistance(userLat, userLng, carLat, carLng)
  }, [userLocation, carLat, carLng])

  const distanceDisplay = useMemo(() => {
    if (distance === null) return null
    if (distance < 1) {
      return localizeDigits(`${Math.round(distance * 1000)}m`)
    }
    return localizeDigits(`${distance.toFixed(1)}km`)
  }, [distance])

  const walkingMinutes = useMemo(() => {
    if (distance === null) return null
    return Math.round((distance / 5) * 60)
  }, [distance])

  return (
    <div className="absolute inset-x-0 bottom-0 z-[1000] p-4 pb-safe-area-inset-bottom">
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{car.name}</CardTitle>
              {car.isAWD && (
                <Badge variant="secondary" className="text-xs">
                  AWD
                </Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {car.licensePlate}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {/* Quick info row */}
          <div className="flex items-center gap-4 text-sm">
            {/* Fuel/Battery */}
            {car.fuelLevel !== undefined && (
              <div className="flex items-center gap-1.5">
                {isElectric ? (
                  <Battery
                    className={cn('h-4 w-4', getFuelColor(car.fuelLevel))}
                  />
                ) : (
                  <Fuel
                    className={cn('h-4 w-4', getFuelColor(car.fuelLevel))}
                  />
                )}
                <span
                  className={cn('font-medium', getFuelColor(car.fuelLevel))}
                >
                  {localizeDigits(`${car.fuelLevel}%`)}
                </span>
              </div>
            )}

            {/* Distance */}
            {distanceDisplay && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{distanceDisplay}</span>
              </div>
            )}

            {/* Walking time */}
            {walkingMinutes !== null && (
              <div className="flex items-center gap-1.5">
                <Navigation className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {t('rentalBanner.walkingTime', {
                    minutes: localizeDigits(String(walkingMinutes)),
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Action button */}
          <Button className="w-full" size="sm" onClick={onReserve}>
            {accountName
              ? t('carDetails.reserveOn', { account: accountName })
              : t('carsMap.reserve')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
